import { useState } from 'react';
import { Card, Select } from '@/ds';
import { EmptyState } from '@/components/EmptyState';
import { useAudit, useAuditFilters, type AuditRow } from '@/api/hooks';
import { formatDateTime } from '@/lib/format';
import { useIsMobile } from '@/lib/useMediaQuery';
import { QueryState } from '@/components/QueryState';

/** Человеческие названия действий. Новое действие показывается своим кодом. */
const ACTION_TITLES: Record<string, string> = {
  'auth.login': 'Вход в приложение',
  'auth.logout': 'Выход из приложения',
  'profile.set_password': 'Резервный пароль задан',
  'profile.clear_password': 'Резервный вход отключён',
  'profile.rename': 'Имя изменено',
  'session.revoke_all': 'Выход на всех устройствах',
};

function actionTitle(action: string): string {
  return ACTION_TITLES[action] ?? action;
}

function Rows({ rows }: { rows: AuditRow[] }) {
  return (
    <div className="kj-scroll-x">
      <table className="kj-table">
        <thead>
          <tr>
            <th>Когда</th>
            <th>Кто</th>
            <th>Действие</th>
            <th>Подробности</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td data-label="Когда" className="kj-mono">{formatDateTime(row.createdAt)}</td>
              <td data-label="Кто" style={{ whiteSpace: 'nowrap' }}>{row.author?.name ?? 'Система'}</td>
              <td data-label="Действие" style={{ color: 'var(--text-strong)' }}>{actionTitle(row.action)}</td>
              <td data-label="Подробности" style={{ color: 'var(--text-muted)' }}>{row.summary ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Cards({ rows }: { rows: AuditRow[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {rows.map((row) => (
        <Card key={row.id} padding="sm">
          <div style={{ font: '500 14px var(--font-text)', color: 'var(--text-strong)' }}>
            {actionTitle(row.action)}
          </div>
          {row.summary ? (
            <div
              style={{
                marginTop: 'var(--space-2)',
                font: '400 13px/1.5 var(--font-text)',
                color: 'var(--text-muted)',
              }}
            >
              {row.summary}
            </div>
          ) : null}
          <div
            style={{
              marginTop: 'var(--space-3)',
              display: 'flex',
              justifyContent: 'space-between',
              gap: 'var(--space-4)',
            }}
          >
            <span style={{ font: '400 12px var(--font-text)', color: 'var(--text-subtle)' }}>
              {row.author?.name ?? 'Система'}
            </span>
            <span className="kj-mono" style={{ fontSize: 'var(--fs-2xs)' }}>
              {formatDateTime(row.createdAt)}
            </span>
          </div>
        </Card>
      ))}
    </div>
  );
}

export function AuditTab() {
  const [userId, setUserId] = useState('');
  const [action, setAction] = useState('');
  const isMobile = useIsMobile();

  const filters = useAuditFilters(true);
  const audit = useAudit(
    { ...(userId ? { userId } : {}), ...(action ? { action } : {}) },
    true,
  );

  const authorOptions = [
    { value: '', label: 'Все авторы' },
    ...(filters.data?.authors ?? []).map((a) => ({ value: a.id, label: a.name })),
  ];
  const actionOptions = [
    { value: '', label: 'Все действия' },
    ...(filters.data?.actions ?? []).map((a) => ({ value: a, label: actionTitle(a) })),
  ];

  const rows = audit.data?.rows ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <p style={{ font: '400 13px/1.5 var(--font-text)', color: 'var(--text-muted)', maxWidth: 620 }}>
        Прав у всех одинаково, поэтому важно видеть, кто что менял. Журнал только читается и не
        удаляется.
      </p>

      <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 200 }}>
          <Select
            label="Автор"
            size={isMobile ? 'lg' : 'sm'}
            options={authorOptions}
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          />
        </div>
        <div style={{ minWidth: 220 }}>
          <Select
            label="Действие"
            size={isMobile ? 'lg' : 'sm'}
            options={actionOptions}
            value={action}
            onChange={(e) => setAction(e.target.value)}
          />
        </div>
      </div>

      {audit.isLoading || audit.isError ? (
        <QueryState query={audit} what="журнал" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="ScrollText"
          title="Записей нет"
          description="Как только вы что-нибудь измените, действие появится здесь: кто, что и когда. С выбранными фильтрами записей тоже может не быть."
        />
      ) : (
        <Card padding={isMobile ? 'none' : 'md'} elevation={isMobile ? 'none' : 'xs'} tone={isMobile ? 'sunken' : 'plain'} style={isMobile ? { background: 'transparent', border: 'none' } : undefined}>
          {isMobile ? <Cards rows={rows} /> : <Rows rows={rows} />}
        </Card>
      )}
    </div>
  );
}
